import { DateTime } from "luxon";
import { SQDCActionStatus } from "@/graphql";
import type { ActionFilter, EventFilter } from "./FilterTypes";
import {
  CreatorFilter,
  DateFilter,
  DescriptionFilter,
  FlagsFilter,
  IDFilter,
  StatusFilter,
} from "./filterImplementations";

export const quickSearchKeywords = [
  "eid",
  "aid",
  "flag",
  "cre",
  "resp",
  "edf",
  "edt",
  "dlf",
  "dlt",
  "edsc",
  "adsc",
  "stat",
] as const;

type NamedOption = { id: number; names: string[] };
type ShortcutContext = {
  flags: NamedOption[];
  eventUsers: NamedOption[];
  actionUsers: NamedOption[];
};
export type ShortcutFilters = { eventFilters: EventFilter[]; actionFilters: ActionFilter[] };

function normalize(value: string) {
  return value
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase();
}

function values(value: string) {
  return value
    .split(",")
    .map((item) => item.trim().replace(/^("|')|("|')$/g, ""))
    .filter(Boolean);
}

function numbers(value: string) {
  const parsed = values(value).map(Number);
  return parsed.length > 0 && parsed.every(Number.isInteger) ? parsed : null;
}

function namedIds(value: string, options: NamedOption[]) {
  const requested = values(value).map(normalize);
  const matches = requested.map((item) =>
    options.find(
      (option) =>
        String(option.id) === item || option.names.some((name) => normalize(name) === item),
    ),
  );
  return requested.length > 0 && matches.every((match) => match != null)
    ? matches.map((match) => match!.id)
    : null;
}

function oneSidedDate(
  value: string,
  boundary: "from" | "to",
): [DateTime | null, DateTime | null] | null {
  const date = DateTime.fromISO(value.trim());
  if (!date.isValid) {
    return null;
  }
  return boundary === "from" ? [date, null] : [null, date];
}

function actionStatuses(value: string) {
  const requested = values(value).map((item) => item.toUpperCase());
  const allowed = Object.values(SQDCActionStatus);
  return requested.length > 0 &&
    requested.every((item): item is SQDCActionStatus => allowed.includes(item as SQDCActionStatus))
    ? requested
    : null;
}

export function parseQuickSearchShortcut(
  input: string,
  context: ShortcutContext,
): ShortcutFilters | null {
  const match = input.match(/^([a-zA-Z]+):(.*)$/s);
  if (match == null) {
    return null;
  }
  const keyword = match[1].toLowerCase();
  const value = match[2].trim();
  if (!(quickSearchKeywords as readonly string[]).includes(keyword) || value === "") {
    return null;
  }

  switch (keyword) {
    case "eid": {
      const ids = numbers(value);
      return ids == null ? null : { eventFilters: [new IDFilter(ids)], actionFilters: [] };
    }
    case "aid": {
      const ids = numbers(value);
      return ids == null ? null : { eventFilters: [], actionFilters: [new IDFilter(ids)] };
    }
    case "flag": {
      const ids = namedIds(value, context.flags);
      return ids == null ? null : { eventFilters: [new FlagsFilter(ids)], actionFilters: [] };
    }
    case "cre": {
      const ids = namedIds(value, context.eventUsers);
      return ids == null ? null : { eventFilters: [new CreatorFilter(ids)], actionFilters: [] };
    }
    case "resp": {
      const ids = namedIds(value, context.actionUsers);
      return ids == null ? null : { eventFilters: [], actionFilters: [new CreatorFilter(ids)] };
    }
    case "edf": {
      const date = oneSidedDate(value, "from");
      return date == null ? null : { eventFilters: [new DateFilter(date)], actionFilters: [] };
    }
    case "edt": {
      const date = oneSidedDate(value, "to");
      return date == null ? null : { eventFilters: [new DateFilter(date)], actionFilters: [] };
    }
    case "dlf": {
      const date = oneSidedDate(value, "from");
      return date == null ? null : { eventFilters: [], actionFilters: [new DateFilter(date)] };
    }
    case "dlt": {
      const date = oneSidedDate(value, "to");
      return date == null ? null : { eventFilters: [], actionFilters: [new DateFilter(date)] };
    }
    case "edsc":
      return { eventFilters: [new DescriptionFilter(value)], actionFilters: [] };
    case "adsc":
      return { eventFilters: [], actionFilters: [new DescriptionFilter(value)] };
    case "stat": {
      const statuses = actionStatuses(value);
      return statuses == null
        ? null
        : { eventFilters: [], actionFilters: [new StatusFilter(statuses)] };
    }
    default:
      return null;
  }
}

export function isKnownQuickSearchShortcut(input: string) {
  const keyword = input.match(/^([a-zA-Z]+):/s)?.[1].toLowerCase();
  return keyword != null && (quickSearchKeywords as readonly string[]).includes(keyword);
}
